import { useState } from "react";
import { Loader2, MailCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import StarField from "../components/StarField";
import { useSEO } from "../hooks/useSEO";

export default function RetainGuru() {
  const [form, setForm] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  useSEO(
    "Retain a Guru — Join the Waitlist | NAKSRA",
    "Join the NAKSRA Retain-a-Guru waitlist. Continual guidance from a vetted Jyotish guru based on your personal fingerprint of planetary transits."
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/waitlist", form);
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-lg border border-[#27272A] bg-[#050507] px-4 py-3 text-sm text-white placeholder-[#52525B] outline-none transition-colors duration-300 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/40";

  return (
    <div className="min-h-screen bg-[#050507] text-[#F8F9FA]">
      <Navbar />
      <main className="relative overflow-hidden">
        <StarField />
        <div className="relative mx-auto max-w-2xl px-6 py-24 sm:py-32" data-testid="retain-guru-page">
          <p className="font-heading text-xs font-medium uppercase tracking-[0.25em] text-[#D4AF37] sm:text-sm">Retain a Guru</p>
          <h1 className="mt-4 font-heading text-4xl font-medium leading-tight tracking-tight sm:text-5xl">
            Your own Jyotish guru, on retainer
          </h1>
          <p className="mt-8 leading-relaxed text-gray-300">
            This service is not yet live as we are still vetting from amongst the best guru applicants in the world to present to you. However you may join the wait-list by entering your information below. Free gifts and the best discounts will be offered to the first 10,000 registered users who express interest in our "Retain a Guru" service and verify their email address.
          </p>

          {done ? (
            <div className="mt-10 rounded-lg border border-[#17B890]/30 bg-[#0C0C10] p-8 text-center" data-testid="waitlist-success">
              <MailCheck className="mx-auto h-8 w-8 text-[#17B890]" />
              <h2 className="mt-4 font-heading text-2xl font-medium">Check your inbox</h2>
              <p className="mt-3 text-sm leading-relaxed text-[#A1A1AA]">
                We've sent a verification link to <span className="text-white">{form.email}</span>. Click it to confirm your spot on the wait-list.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="mt-10 space-y-5 rounded-lg border border-white/10 bg-[#0C0C10] p-8" data-testid="waitlist-form">
              <div>
                <label htmlFor="wl-name" className="mb-2 block text-xs uppercase tracking-[0.2em] text-[#A1A1AA]">Full name</label>
                <input id="wl-name" data-testid="waitlist-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} placeholder="Your name" />
              </div>
              <div>
                <label htmlFor="wl-email" className="mb-2 block text-xs uppercase tracking-[0.2em] text-[#A1A1AA]">Email address</label>
                <input id="wl-email" data-testid="waitlist-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} placeholder="you@example.com" />
              </div>
              <button type="submit" disabled={loading} data-testid="waitlist-submit-btn" className="flex w-full items-center justify-center gap-2 rounded-full bg-[#D4AF37] px-8 py-3 text-sm font-semibold text-black transition-colors duration-300 hover:bg-[#FBE18D] active:scale-[0.98] disabled:opacity-60">
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                {loading ? "Joining…" : "Join the wait-list"}
              </button>
            </form>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
